import React, { useEffect, useMemo, useState } from 'react';

import {
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    message,
} from 'antd';

import dayjs from 'dayjs';

import { api } from '../../../http/API';
import { cultureAPI } from '../../../http/CultureAPI';
import { UserCulture } from '../../../models/Culture';
import { FieldCropHistory, FieldCropHistoryInput } from '../../../models/FieldCropHistory';

const { Option } = Select;
const { TextArea } = Input;

interface Props {
    open: boolean;
    fieldId: number;
    season: FieldCropHistory | null;
    onCancel: () => void;
    onSaved: () => void;
}

const STATUS_OPTIONS = [
    { value: 'planned', label: 'Запланирован' },
    { value: 'sown', label: 'Посеян' },
    { value: 'harvested', label: 'Убран' },
];

// Sentinel для «сохранённая культура отсутствует в Моих культурах»
// (пользователь удалил её после сохранения сезона).
const FALLBACK_KEY = -1;

const userCultureLabel = (uc: UserCulture): string => {
    const cultureName = uc.culture?.name || `Культура #${uc.culture_id}`;
    return uc.sort ? `${cultureName} — ${uc.sort.name}` : cultureName;
};

const FieldSeasonModal = (props: Props) => {
    const { open, fieldId, season, onCancel, onSaved } = props;
    const [form] = Form.useForm();
    const [myCultures, setMyCultures] = useState<UserCulture[]>([]);
    const [myCulturesLoading, setMyCulturesLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [deleting, setDeleting] = useState<boolean>(false);

    const isEdit = !!(season && season.season_harvest);

    // Загрузка «моих культур» (личный список пользователя из Настроек).
    useEffect(() => {
        if (!open) return;
        setMyCulturesLoading(true);
        cultureAPI
            .GetMyCultures()
            .then((data) => setMyCultures(data || []))
            .catch(() => setMyCultures([]))
            .finally(() => setMyCulturesLoading(false));
    }, [open]);

    // Сортировка по имени культуры (как в ReportHeader).
    const sortedCultures = useMemo(
        () =>
            [...myCultures].sort((a, b) =>
                (a.culture?.name || '').localeCompare(b.culture?.name || ''),
            ),
        [myCultures],
    );

    // Fallback: если у сохранённого сезона есть culture_id, которого нет в «Моих культурах»,
    // добавим его отдельным пунктом, чтобы редактирование не сломалось.
    const fallbackCulture = useMemo(() => {
        if (!season || !season.culture_id) return null;
        const found = myCultures.find((uc) => uc.culture_id === season.culture_id);
        if (found) return null;
        return {
            key: FALLBACK_KEY,
            label: `Культура #${season.culture_id}${
                season.sort_id ? ` — сорт #${season.sort_id}` : ''
            } (не в моих культурах)`,
        };
    }, [season, myCultures]);

    // Заполнение формы при открытии.
    useEffect(() => {
        if (!open) return;
        if (season) {
            // Ищем соответствующую UserCulture по (culture_id, sort_id).
            // Если нашли — ставим её id. Если нет — fallback.
            let userCultureId: number | undefined = undefined;
            const match = myCultures.find(
                (uc) =>
                    uc.culture_id === season.culture_id &&
                    (uc.sort_id ?? null) === (season.sort_id ?? null),
            );
            if (match) {
                userCultureId = match.id;
            } else {
                const loose = myCultures.find((uc) => uc.culture_id === season.culture_id);
                if (loose) {
                    userCultureId = loose.id;
                } else if (season.culture_id) {
                    userCultureId = FALLBACK_KEY;
                }
            }

            form.setFieldsValue({
                season_harvest: season.season_harvest,
                season_sowing: season.season_sowing,
                user_culture_id: userCultureId,
                sowing_date: season.sowing_date ? dayjs(season.sowing_date) : null,
                harvest_date: season.harvest_date ? dayjs(season.harvest_date) : null,
                status: season.status || 'planned',
                actual_yield: season.actual_yield ?? undefined,
                notes: season.notes ?? '',
            });
        } else {
            form.resetFields();
            form.setFieldsValue({
                season_harvest: new Date().getFullYear(),
                season_sowing: new Date().getFullYear(),
                status: 'planned',
            });
        }
    }, [open, season, form, myCultures]);

    const handleOk = () => {
        form.validateFields()
            .then((values) => {
                const year = isEdit ? season!.season_harvest : values.season_harvest;

                // Резолвим culture_id / sort_id из выбранной UserCulture.
                // Fallback: если выбрана сохранённая «чуждая» культура — используем её.
                let cultureId: number | undefined;
                let sortId: number | null = null;

                if (values.user_culture_id === FALLBACK_KEY) {
                    cultureId = season?.culture_id;
                    sortId = season?.sort_id ?? null;
                } else {
                    const uc = myCultures.find((x) => x.id === values.user_culture_id);
                    if (!uc) {
                        message.error('Культура не найдена в списке');
                        return;
                    }
                    cultureId = uc.culture_id;
                    sortId = uc.sort_id ?? null;
                }

                if (!cultureId) {
                    message.error('Не удалось определить культуру');
                    return;
                }

                const payload: FieldCropHistoryInput = {
                    season_sowing: values.season_sowing,
                    culture_id: cultureId,
                    sort_id: sortId,
                    status: values.status || 'planned',
                    sowing_date: values.sowing_date
                        ? values.sowing_date.format('YYYY-MM-DD')
                        : null,
                    harvest_date: values.harvest_date
                        ? values.harvest_date.format('YYYY-MM-DD')
                        : null,
                    actual_yield: values.actual_yield ?? null,
                    notes: values.notes || null,
                };

                setLoading(true);
                api
                    .UpsertFieldSeason(fieldId, year, payload)
                    .then(() => {
                        message.success(isEdit ? 'Сезон обновлён' : 'Сезон создан');
                        onSaved();
                    })
                    .catch(() => message.error('Не удалось сохранить сезон'))
                    .finally(() => setLoading(false));
            })
            .catch(() => undefined);
    };

    const handleDelete = () => {
        if (!isEdit) return;
        setDeleting(true);
        api
            .DeleteFieldSeason(fieldId, season!.season_harvest)
            .then(() => {
                message.success('Сезон удалён');
                onSaved();
            })
            .catch(() => message.error('Не удалось удалить сезон'))
            .finally(() => setDeleting(false));
    };

    return (
        <Modal
            title={isEdit ? `Сезон ${season!.season_harvest}` : 'Новый сезон'}
            open={open}
            onCancel={onCancel}
            onOk={handleOk}
            confirmLoading={loading}
            okText="Сохранить"
            cancelText="Отмена"
            width={640}
            destroyOnClose
            footer={
                <Space style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                        {isEdit && (
                            <Popconfirm
                                title="Удалить сезон?"
                                okText="Да"
                                cancelText="Нет"
                                onConfirm={handleDelete}
                            >
                                <Button danger loading={deleting}>
                                    Удалить сезон
                                </Button>
                            </Popconfirm>
                        )}
                    </span>
                    <Space>
                        <Button onClick={onCancel}>Отмена</Button>
                        <Button type="primary" onClick={handleOk} loading={loading}>
                            Сохранить
                        </Button>
                    </Space>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    name="season_harvest"
                    label="Год уборки"
                    rules={[{ required: true, message: 'Укажите год уборки' }]}
                    extra={isEdit ? 'Год уборки не меняется после создания' : 'Например: 2025'}
                >
                    <InputNumber
                        min={1900}
                        max={2100}
                        style={{ width: 160 }}
                        disabled={isEdit}
                    />
                </Form.Item>

                <Form.Item
                    name="season_sowing"
                    label="Год посева"
                    rules={[{ required: true, message: 'Укажите год посева' }]}
                    extra="Для озимых — предыдущий год"
                >
                    <InputNumber min={1900} max={2100} style={{ width: 160 }} />
                </Form.Item>

                <Form.Item
                    name="user_culture_id"
                    label="Культура"
                    rules={[{ required: true, message: 'Выберите культуру' }]}
                    extra={
                        myCultures.length === 0 && !myCulturesLoading ? (
                            <span>
                                Список пуст. Добавьте культуры в{' '}
                                <a href="/settings">Настройках</a>.
                            </span>
                        ) : undefined
                    }
                >
                    <Select
                        showSearch
                        placeholder={
                            myCulturesLoading ? 'Загрузка…' : 'Выберите культуру из моих'
                        }
                        loading={myCulturesLoading}
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            String(option?.children ?? '')
                                .toLowerCase()
                                .indexOf(input.toLowerCase()) >= 0
                        }
                    >
                        {sortedCultures.map((uc) => (
                            <Option key={uc.id} value={uc.id}>
                                {userCultureLabel(uc)}
                            </Option>
                        ))}
                        {fallbackCulture && (
                            <Option key={fallbackCulture.key} value={fallbackCulture.key}>
                                {fallbackCulture.label}
                            </Option>
                        )}
                    </Select>
                </Form.Item>

                <Form.Item name="status" label="Статус">
                    <Select options={STATUS_OPTIONS} />
                </Form.Item>

                <Form.Item name="sowing_date" label="Дата посева">
                    <DatePicker style={{ width: 200 }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item name="harvest_date" label="Дата уборки">
                    <DatePicker style={{ width: 200 }} format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item
                    name="actual_yield"
                    label="Фактическая урожайность, ц/га"
                    extra="Опционально"
                >
                    <InputNumber min={0} step={0.1} style={{ width: 200 }} />
                </Form.Item>

                <Form.Item name="notes" label="Заметки">
                    <TextArea rows={3} placeholder="Опционально" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default FieldSeasonModal;
